import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Alert, AuthCard, Button, FieldError, FieldRow, Input, Label } from '@sentinel/ui';
import { requestSchema, type RequestInput } from '../features/magic-link/schema';
import { useSendMagicLink } from '../features/magic-link/use-send';

const searchSchema = z.object({ return_to: z.string().optional() });

type ViewState = 'idle' | 'sent';

function getStatus(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}

function MagicLinkRequestPage() {
  const { t } = useTranslation('magic-link');
  const send = useSendMagicLink();
  const [view, setView] = useState<ViewState>('idle');
  const [topError, setTopError] = useState<string | null>(null);
  const { return_to } = Route.useSearch();

  const form = useForm<RequestInput>({ resolver: zodResolver(requestSchema) });

  async function onSubmit(values: RequestInput) {
    setTopError(null);
    try {
      await send.mutateAsync({ email: values.email, ...(return_to ? { return_to } : {}) });
      setView('sent');
    } catch (e) {
      const status = getStatus(e);
      if (status === 429) {
        setTopError(t('rate_limited'));
        return;
      }
      setTopError(t('generic_error'));
    }
  }

  if (view === 'sent') {
    return (
      <AuthCard title={t('sent_title')}>
        <p role="status" aria-live="polite">{t('sent_message')}</p>
        <p>{t('sent_check_inbox')}</p>
        <p style={{ textAlign: 'center' }}>
          <Link to="/login">{t('back_to_login')}</Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('request_title')} subtitle={t('request_subtitle')}>
      {topError ? (
        <Alert type="error" role="alert">
          {topError}
        </Alert>
      ) : null}

      <form
        onSubmit={form.handleSubmit(onSubmit)}
        style={{ display: 'grid', gap: '0.75rem' }}
      >
        <FieldRow>
          <Label htmlFor="email">{t('email_label')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            aria-invalid={form.formState.errors.email ? 'true' : undefined}
            {...form.register('email')}
          />
          <FieldError message={form.formState.errors.email?.message} />
        </FieldRow>
        <Button type="submit" disabled={send.isPending}>
          {t('send_button')}
        </Button>
      </form>

      <p style={{ textAlign: 'center' }}>
        <Link to="/login">{t('back_to_login')}</Link>
      </p>
    </AuthCard>
  );
}

export const Route = createFileRoute('/magic-link/')({
  validateSearch: searchSchema,
  component: MagicLinkRequestPage,
});
